import { useEffect, useRef, useState, type ReactNode } from "react";

/** Series colours in fixed order (tokens `--chart-1..3`, validated for colour-blind separation in both themes). */
export const SERIES_COLORS = ["hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))"] as const;
export const GRID = "hsl(var(--border))";
export const AXIS_TEXT = "hsl(var(--muted-foreground))";
export const SURFACE = "hsl(var(--surface))";

export interface ChartSeries { key: string; label: string; color: string }
export interface TipRow { color: string; label: string; value: string }
export interface Tip { x: number; title: string; rows: TipRow[] }

/** The width of the element, so a chart redraws to fit its card (a fixed width where ResizeObserver is missing, e.g. in tests). */
export function useWidth(fallback = 640): [React.RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [w, setW] = useState(fallback);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([e]) => { const next = Math.floor(e.contentRect.width); if (next > 0) setW(next); });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

/** A round number at or above the largest value, with clean ticks (0, 25, 50 ... or 0, 5, 10 ...). */
export function niceScale(max: number, ticks = 4): { top: number; ticks: number[] } {
  if (max <= 0) return { top: ticks, ticks: Array.from({ length: ticks + 1 }, (_, i) => i) };
  const raw = max / ticks;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? 10 * mag;
  const top = step * Math.ceil(max / step);
  return { top, ticks: Array.from({ length: Math.round(top / step) + 1 }, (_, i) => i * step) };
}

export function Legend({ series }: { series: ChartSeries[] }) {
  if (series.length < 2) return null;       // one series: the title already says what is plotted
  return (
    <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground" aria-label="Legend">
      {series.map((s) => (
        <li key={s.key} className="inline-flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: s.color }} aria-hidden />
          {s.label}
        </li>
      ))}
    </ul>
  );
}

/** The one tooltip: the value leads, the series name follows; each row is keyed by a short stroke of the series colour. */
export function Tooltip({ tip, width }: { tip: Tip | null; width: number }) {
  if (!tip) return null;
  const flip = tip.x > width / 2;
  return (
    <div role="status" className="pointer-events-none absolute top-2 z-10 rounded-lg border border-border bg-surface px-3 py-2 text-xs shadow-md"
      style={flip ? { right: Math.max(0, width - tip.x + 12) } : { left: tip.x + 12 }}>
      <p className="mb-1 font-medium text-foreground">{tip.title}</p>
      {tip.rows.map((r) => (
        <p key={r.label} className="flex items-center gap-2 leading-5">
          <span className="inline-block w-3 h-0.5 rounded" style={{ background: r.color }} aria-hidden />
          <strong className="font-semibold text-foreground tabular-nums">{r.value}</strong>
          <span className="text-muted-foreground">{r.label}</span>
        </p>
      ))}
    </div>
  );
}

/** A titled card around one chart, with a Chart / Table switch so every number is reachable without the graphic. */
export function ChartCard({ title, subtitle, legend, table, children, testId }: {
  title: string; subtitle?: string; legend?: ChartSeries[]; table: { columns: string[]; rows: (string | number)[][] }; children: ReactNode; testId?: string;
}) {
  const [asTable, setAsTable] = useState(false);
  return (
    <section aria-label={title} data-testid={testId} className="rounded-xl border border-border bg-surface p-4 space-y-3 min-w-0">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-foreground">{title}</h2>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        </div>
        <button type="button" onClick={() => setAsTable((v) => !v)} aria-pressed={asTable}
          className="rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:text-foreground">
          {asTable ? "Show chart" : "Show table"}
        </button>
      </div>
      {legend && <Legend series={legend} />}
      {asTable ? (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-muted-foreground">{table.columns.map((c, i) => <th key={c} className={`py-1.5 pr-3 font-medium ${i ? "text-right" : ""}`}>{c}</th>)}</tr></thead>
            <tbody>{table.rows.map((r, ri) => (
              <tr key={ri} className="border-t border-border">{r.map((v, i) => <td key={i} className={`py-1.5 pr-3 tabular-nums ${i ? "text-right" : ""}`}>{v}</td>)}</tr>
            ))}</tbody>
          </table>
        </div>
      ) : children}
    </section>
  );
}
